import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { personalInfo } from '../../data/portfolio';

const FooterSection = styled.footer`
  background: linear-gradient(180deg, ${({ theme }) => theme.colors.background} 0%, ${({ theme }) => theme.colors.backgroundCard} 100%);
  color: ${({ theme }) => theme.colors.muted};
  text-align: center;
  padding: 48px 20px 32px;
  border-top: 1px solid var(--clr-divider);
`;

const FooterContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const SocialRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 28px;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--clr-surface-bg);
  border: 1px solid var(--clr-surface-border);
  color: var(--clr-text-muted);
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: var(--clr-accent-subtle);
    border-color: var(--clr-accent-border-h);
    color: ${({ theme }) => theme.colors.accent};
    transform: translateY(-2px);
  }
`;

const Divider = styled.div`
  width: 40px;
  height: 1px;
  background: var(--clr-divider);
  margin: 0 auto 20px;
`;

const FooterName = styled.p`
  font-size: 14px;
  color: var(--clr-text-muted);
  margin-bottom: 6px;

  strong {
    color: var(--clr-text-bright);
    font-weight: 600;
  }
`;

const FooterNote = styled.p`
  font-size: 12px;
  color: var(--clr-text-faint);
`;

const BackToTop = styled.button`
  display: block;
  margin: 24px auto 0;
  padding: 8px 20px;
  background: none;
  border: 1px solid var(--clr-divider);
  border-radius: 20px;
  color: var(--clr-text-faint);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 1px;

  &:hover {
    border-color: var(--clr-accent-border-h);
    color: var(--clr-text-dim);
  }
`;

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <FooterSection>
      <FooterContent>
        <SocialRow>
          {personalInfo.contact.linkedin && (
            <SocialLink href={personalInfo.contact.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              in
            </SocialLink>
          )}
          {personalInfo.contact.github && (
            <SocialLink href={personalInfo.contact.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              ⌘
            </SocialLink>
          )}
          {personalInfo.contact.gitlab && (
            <SocialLink href={personalInfo.contact.gitlab} target="_blank" rel="noopener noreferrer" aria-label="GitLab">
              ◆
            </SocialLink>
          )}
          <SocialLink href={`mailto:${personalInfo.contact.email}`} aria-label="Email">
            ✉
          </SocialLink>
        </SocialRow>
        <Divider />
        <FooterName>
          <strong>{personalInfo.name}</strong> &bull; {t('footer.role')}
        </FooterName>
        <FooterNote>© {new Date().getFullYear()} &bull; Montreal, QC</FooterNote>
        <BackToTop onClick={scrollToTop}>↑ {t('footer.backToTop')}</BackToTop>
      </FooterContent>
    </FooterSection>
  );
};

export default Footer;